/**
 * ProfilePictureDialog
 *
 * Modal used by the header avatar to let a signed-in user upload,
 * crop and save a JPG/PNG/WebP profile picture. UI-only concern —
 * authentication and account state are untouched.
 *
 * Flow:
 *   1. User clicks their avatar → dialog opens with the current
 *      picture (custom OR Google OR initials).
 *   2. User picks a file. We validate mime + size and load it into
 *      the cropper (react-easy-crop).
 *   3. User pans/zooms. On Save we produce a 512×512 JPEG data URI
 *      via a canvas and POST it to /api/auth/profile/picture.
 *   4. Optionally "Remove custom photo" if the user has one; this
 *      falls back to the Google picture (if any) via DELETE.
 *
 * No changes to auth, session, or account handling. Refreshes the
 * global user context on save so avatars across the app re-render
 * immediately.
 */
import { useCallback, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { toast } from 'sonner';
import { Camera, Trash2, Loader2, Upload } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { authApi } from '../lib/api';

const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_INPUT_BYTES = 8 * 1024 * 1024; // 8 MB pre-crop
const OUTPUT_SIZE = 512;                  // px, square

/** Render a cropped square from the original image element onto a
 * fixed-size square canvas and return its JPEG data URI. */
async function generateCroppedDataUrl(imageSrc, croppedAreaPixels) {
    const image = await loadImage(imageSrc);
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        OUTPUT_SIZE,
        OUTPUT_SIZE,
    );
    // 0.85 JPEG quality — good visual/size trade-off, keeps us
    // well under the backend's 700 KB cap.
    return canvas.toDataURL('image/jpeg', 0.85);
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

export function ProfilePictureDialog({ open, onOpenChange, user, onUpdated }) {
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [saving, setSaving] = useState(false);
    const [removing, setRemoving] = useState(false);
    const fileInputRef = useRef(null);

    const resetPickerState = () => {
        setImageSrc(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDialogChange = (nextOpen) => {
        if (!nextOpen) resetPickerState();
        onOpenChange(nextOpen);
    };

    const handleFileSelected = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!ACCEPTED_MIME.includes(file.type)) {
            toast.error('Please choose a JPG, PNG or WebP image');
            return;
        }
        if (file.size > MAX_INPUT_BYTES) {
            toast.error('Image is too large (max 8 MB)');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setImageSrc(reader.result);
        reader.readAsDataURL(file);
    };

    const onCropComplete = useCallback((_area, areaPixels) => {
        setCroppedAreaPixels(areaPixels);
    }, []);

    const handleSave = async () => {
        if (!imageSrc || !croppedAreaPixels) {
            toast.error('Choose a picture first');
            return;
        }
        setSaving(true);
        try {
            const dataUrl = await generateCroppedDataUrl(imageSrc, croppedAreaPixels);
            const res = await authApi.uploadProfilePicture(dataUrl);
            toast.success('Profile picture updated');
            if (onUpdated) onUpdated(res.data);
            resetPickerState();
            onOpenChange(false);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Upload failed';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        setRemoving(true);
        try {
            const res = await authApi.removeProfilePicture();
            toast.success('Profile picture removed');
            if (onUpdated) onUpdated(res.data);
            resetPickerState();
            onOpenChange(false);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Remove failed';
            toast.error(msg);
        } finally {
            setRemoving(false);
        }
    };

    const initials = (user?.name || '?').trim().charAt(0).toUpperCase();
    const currentPicture = user?.picture;

    return (
        <Dialog open={open} onOpenChange={handleDialogChange}>
            <DialogContent className="sm:max-w-md" data-testid="profile-picture-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Camera className="h-4 w-4 text-slate-500" />
                        Profile picture
                    </DialogTitle>
                    <DialogDescription>
                        Choose a JPG, PNG or WebP image and drag to crop. Your picture will be
                        shown next to your name across the app.
                    </DialogDescription>
                </DialogHeader>

                {!imageSrc ? (
                    <div className="space-y-4">
                        <div className="flex justify-center py-2">
                            {currentPicture ? (
                                <img
                                    src={currentPicture}
                                    alt="Current profile"
                                    className="w-28 h-28 rounded-full object-cover border border-slate-200"
                                    data-testid="current-profile-picture"
                                />
                            ) : (
                                <div
                                    className="w-28 h-28 rounded-full bg-slate-900 text-white flex items-center justify-center text-4xl font-medium"
                                    data-testid="current-profile-initials"
                                >
                                    {initials}
                                </div>
                            )}
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPTED_MIME.join(',')}
                            onChange={handleFileSelected}
                            className="hidden"
                            data-testid="profile-picture-file-input"
                        />
                        <Button
                            variant="default"
                            className="w-full"
                            onClick={() => fileInputRef.current?.click()}
                            data-testid="profile-picture-choose-btn"
                        >
                            <Upload className="h-4 w-4 mr-2" />
                            Choose a new picture
                        </Button>

                        {user?.picture_is_custom && (
                            <Button
                                variant="outline"
                                className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                onClick={handleRemove}
                                disabled={removing}
                                data-testid="profile-picture-remove-btn"
                            >
                                {removing ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Trash2 className="h-4 w-4 mr-2" />
                                )}
                                Remove custom picture
                                {user?.google_picture_url ? ' (revert to Google)' : ''}
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="relative w-full h-64 bg-slate-900 rounded-lg overflow-hidden" data-testid="profile-picture-cropper">
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape="round"
                                showGrid={false}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-slate-500 min-w-[40px]">Zoom</span>
                            <Slider
                                value={[zoom]}
                                onValueChange={(v) => setZoom(v[0])}
                                min={1}
                                max={3}
                                step={0.05}
                                className="flex-1"
                                data-testid="profile-picture-zoom-slider"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <Button
                                variant="outline"
                                onClick={resetPickerState}
                                disabled={saving}
                                data-testid="profile-picture-back-btn"
                            >
                                Pick different image
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                data-testid="profile-picture-save-btn"
                            >
                                {saving ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Camera className="h-4 w-4 mr-2" />
                                )}
                                Save picture
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default ProfilePictureDialog;
